/**
 * The platform SCOPE MODEL — one declarative shape every app feeds to
 * `<kit-scope-nav>`.
 *
 * Scope is a strict hierarchy, outermost first:
 *
 *   space    — the top level a person is acting in: personal · family ·
 *              business · community · public. It is carried by COLOR (the
 *              mark's accent), not by a word, so switching space is felt as a
 *              change of the logo, not a change of the chrome.
 *   app      — the platform application: Career · DNA · … The app name is the
 *              UPPER line of the lockup, right of the mark.
 *   section  — the in-app area: Workshops · Bible · Kompass · … The section is
 *              the LOWER line of the lockup — the big, current one.
 *   path     — everything deeper: sub-area, entity, step. Rendered as a
 *              separator-joined trail after the lockup.
 *
 * Every level is a `ScopeSegment`, and every segment may carry `choices` — the
 * siblings reachable at that level. A segment with choices renders as a
 * dropdown; without, as static text. That single rule is what makes the DNA
 * multi-level dropdown path and the career single-level breadcrumb the SAME
 * component with different data.
 *
 * The model is ArkType-validated on every set (property or `scope` attribute
 * JSON) — nav data arrives from routers, stores and SSR strings, so it is
 * untrusted by construction.
 */

import { type } from "arktype";
import { SPACES, type Space } from "../brand/spaces";
import type { ScopeNode } from "./scope-resolution";

/** The four scope levels, outermost first. */
export const SCOPE_LEVELS = ["space", "app", "section", "path"] as const;
export type ScopeLevel = (typeof SCOPE_LEVELS)[number];

/**
 * One sibling inside a segment's dropdown.
 *
 * A navigable choice sets `url` (rendered as a real `<a href>` — middle-click
 * and "open in new tab" keep working) or is identified by `id` alone (rendered
 * as a `<button>`; the host app acts on the `kit-scope-select` event).
 * A `heading` choice is a non-interactive group label.
 */
export interface ScopeChoice {
  /** Display text. */
  label: string;
  /** Stable identifier echoed in the select event. Required for button choices. */
  id?: string;
  /** Navigation target — renders a real anchor. */
  url?: string;
  /** Marks the currently-active sibling (`aria-current`). */
  current?: boolean;
  /** A second, muted line under the label (DNA's space descriptions). */
  hint?: string;
  /** Non-interactive group label; carries no `url`/`id`. */
  heading?: boolean;
  /**
   * Host-app delegated verb, mirrored onto the affordance as `data-act`.
   * The element ALSO dispatches `kit-scope-select` — the two are independent
   * seams, so an app with a router delegate keeps its one listener.
   */
  act?: string;
  /** Extra `data-*` payload for the host verb (keys are used verbatim). */
  data?: Record<string, string>;
}

/** One level of the scope path. */
export interface ScopeSegment {
  /** Display text for this level. */
  label: string;
  /**
   * Stable LEVEL identity for the cascading resolution rule (`workshop`, `step`).
   * It survives a context switch — that is how a resolver recognises what it is
   * being asked about. Absent ⇒ the level name (`section`, `path:1`).
   */
  key?: string;
  /** WHICH node sits at this level. Absent ⇒ the `current` choice's id/url, else the label. */
  id?: string;
  /** True when `label` is user/domain data rather than app chrome (renders `lang`). */
  content?: boolean;
  /** Accessible name of this segment's dropdown ("Bereich wechseln"). */
  menuLabel?: string;
  /** Siblings at this level. Present ⇒ the segment is a dropdown. */
  choices?: ScopeChoice[];
}

/** The whole navigational scope of the current view. */
export interface ScopeModel {
  /** Top scope. Colors the mark's accent; `null`/absent leaves it ink. */
  space?: Space | null;
  /** Optional VISIBLE top-scope segment (most apps leave the space to the color alone). */
  spaceSegment?: ScopeSegment;
  /** The application — the upper lockup line. */
  app: ScopeSegment;
  /** The in-app area — the lower, current lockup line. */
  section?: ScopeSegment;
  /** Deeper trail: sub-area, entity, step … */
  path?: ScopeSegment[];
  /** Brand composition for the mark (see `marks.ts`). Defaults to `platform-p`. */
  mark?: string;
  /** Where the mark links to. Absent ⇒ the mark is not a link. */
  homeUrl?: string;
}

const choiceSchema = type({
  label: "string > 0",
  "id?": "string > 0",
  "url?": "string > 0",
  "current?": "boolean",
  "hint?": "string > 0",
  "heading?": "boolean",
  "act?": "string > 0",
  "data?": "Record<string, string>",
});

const segmentSchema = type({
  label: "string > 0",
  "key?": "string > 0",
  "id?": "string > 0",
  "content?": "boolean",
  "menuLabel?": "string > 0",
  "choices?": choiceSchema.array(),
});

/**
 * The wire/authoring schema. Kept in sync with `SPACES` by a test — ArkType
 * string syntax cannot spread a const tuple without losing inference.
 */
const scopeModelSchema = type({
  "space?": "'personal' | 'business' | 'family' | 'community' | 'public' | null",
  "spaceSegment?": segmentSchema,
  app: segmentSchema,
  "section?": segmentSchema,
  "path?": segmentSchema.array(),
  "mark?": "string > 0",
  "homeUrl?": "string > 0",
});

/** The space literals the schema accepts — asserted equal to `SPACES` in tests. */
export const SCHEMA_SPACES: readonly string[] = [
  "personal",
  "business",
  "family",
  "community",
  "public",
];

// Compile-time parity: whatever the schema infers must BE a ScopeModel.
type Inferred = typeof scopeModelSchema.infer;
const _inferredIsScopeModel: (m: Inferred) => ScopeModel = (m) => m;
void _inferredIsScopeModel;

/** Thrown when a scope model fails validation. Carries the raw input. */
export class ScopeModelError extends Error {
  readonly raw: unknown;

  constructor(summary: string, raw: unknown) {
    super(
      `[web-kit] invalid scope model — nav data must name its levels ` +
        `(app is required; every segment needs a non-empty label): ${summary}`
    );
    this.name = "ScopeModelError";
    this.raw = raw;
  }
}

/** Validate a scope model object. Throws `ScopeModelError` naming every violation. */
export function parseScope(raw: unknown): ScopeModel {
  const out = scopeModelSchema(raw);
  if (out instanceof type.errors) throw new ScopeModelError(out.summary, raw);
  return out as ScopeModel;
}

/** Validate a scope model carried as a JSON string (the `scope` attribute / SSR seam). */
export function parseScopeJson(raw: string): ScopeModel {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch (error) {
    throw new ScopeModelError(
      `the scope attribute is not JSON (${(error as Error).message})`,
      raw
    );
  }
  return parseScope(value);
}

/** A segment paired with the level it occupies — what the element renders from. */
export interface LeveledSegment {
  level: ScopeLevel;
  /** Index within its level (0 except for `path` segments). */
  index: number;
  segment: ScopeSegment;
}

/**
 * The trail AFTER the lockup: the deeper path segments, in order.
 * (`space`/`app`/`section` live in the lockup and are rendered separately.)
 */
export function pathSegments(model: ScopeModel): LeveledSegment[] {
  return (model.path ?? []).map((segment, index) => ({
    level: "path" as const,
    index,
    segment,
  }));
}

/** Every segment of the model, outermost first — the agent-legible full scope. */
export function allSegments(model: ScopeModel): LeveledSegment[] {
  const out: LeveledSegment[] = [];
  if (model.spaceSegment)
    out.push({ level: "space", index: 0, segment: model.spaceSegment });
  out.push({ level: "app", index: 0, segment: model.app });
  if (model.section)
    out.push({ level: "section", index: 0, segment: model.section });
  return [...out, ...pathSegments(model)];
}

/** `personal › Career › Workshops › …` — the flat textual scope, for aria and logs. */
export function scopeLabel(model: ScopeModel, separator = " › "): string {
  return allSegments(model)
    .map(({ segment }) => segment.label)
    .join(separator);
}

/**
 * The model as a resolvable CHAIN — the input of the cascading resolution rule
 * (`scope-resolution.ts`). Every segment becomes one node, outermost first.
 *
 * A segment carries its own route implicitly: the sibling it marks `current` IS
 * this level's current node, so a model that already highlights where you are
 * needs nothing extra to become resolvable.
 */
export function scopeChain(model: ScopeModel): ScopeNode[] {
  return allSegments(model).map(({ level, index, segment }) =>
    segmentNode(segment, level, index)
  );
}

/** The segment occupying a level, or `undefined` when the model has none there. */
export function segmentAt(
  model: ScopeModel,
  level: ScopeLevel,
  index: number
): ScopeSegment | undefined {
  return allSegments(model).find(
    (entry) => entry.level === level && entry.index === index
  )?.segment;
}

/** Where a given level sits in {@link scopeChain} — the switch's origin index. */
export function chainIndexOf(
  model: ScopeModel,
  level: ScopeLevel,
  index: number
): number {
  return allSegments(model).findIndex(
    (entry) => entry.level === level && entry.index === index
  );
}

/** One segment as a chain node (its `current` choice supplies id and route). */
export function segmentNode(
  segment: ScopeSegment,
  level: ScopeLevel,
  index: number
): ScopeNode {
  const current = segment.choices?.find((choice) => choice.current === true);
  const url = current?.url;
  return {
    key: segment.key ?? levelKey(level, index),
    id: segment.id ?? current?.id ?? current?.url ?? segment.label,
    label: segment.label,
    ...(url !== undefined ? { url } : {}),
  };
}

/** One chosen sibling as a chain node — the node the switch puts at that level. */
export function choiceNode(choice: ScopeChoice, key: string): ScopeNode {
  return {
    key,
    id: choice.id ?? choice.url ?? choice.label,
    label: choice.label,
    ...(choice.url !== undefined ? { url: choice.url } : {}),
  };
}

/** The default level identity: the level name, disambiguated for deep path segments. */
export function levelKey(level: ScopeLevel, index: number): string {
  return level === "path" ? `path:${index}` : level;
}

/** True when `value` names one of the five platform spaces. */
export function isKnownSpace(value: unknown): value is Space {
  return (
    typeof value === "string" && (SPACES as readonly string[]).includes(value)
  );
}
